import { useSnackbar } from "notistack";
import { useToken } from "~/providers/token";
import { Fragment, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { VpnKey as VpnKeyIcon } from "@mui/icons-material";
import { Code } from "~/components/code";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";

type CredentialsDownloaderState = {
  base64: boolean;
  generated: boolean;
  credentials: string;
};

interface IntegrationSetupDetailCredentialsDownloaderProps {
  scope: SpaceScope | OrganizationScope;
}

export const IntegrationSetupDetailCredentialsDownloader = (
  props: IntegrationSetupDetailCredentialsDownloaderProps,
) => {
  const { enqueueSnackbar } = useSnackbar();
  const { genToken, exchangeToken } = useToken();
  const [state, setState] = useState<CredentialsDownloaderState>({
    base64: false,
    generated: false,
    credentials: "",
  });

  const getAgentCredentials = async () => {
    const token = await genToken({ spaceMrn: props.scope.mrn, expiresIn: 120 });
    return exchangeToken(token);
  };

  const handleGenerateClick = async () => {
    try {
      const creds = await getAgentCredentials();
      setState((prevState) => ({
        ...prevState,
        generated: true,
        credentials: JSON.stringify(creds),
      }));
    } catch (error) {
      enqueueSnackbar("Failed to generate new credentials", {
        variant: "error",
      });
    }
  };

  const toggleBase64 = () => {
    setState((prevState) => ({
      ...prevState,
      base64: !prevState.base64,
    }));
  };

  const encodeBase64 = (val: string) => {
    if (state.base64) {
      return window.btoa(val);
    }
    return val;
  };

  return (
    <Fragment>
      <Typography variant="h4">Credentials</Typography>
      <Box mt={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerateClick}
          sx={{ mr: 2 }}
          endIcon={<VpnKeyIcon />}
        >
          Generate new credentials
        </Button>

        <FormControlLabel
          control={
            <Checkbox
              checked={state.base64}
              onClick={toggleBase64}
              value="base64"
            />
          }
          label="Base64-encoded"
        />
      </Box>
      <Code copyButton className="shell">
        {encodeBase64(state.credentials)}
      </Code>
    </Fragment>
  );
};
